import React, { type ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  type Amenities,
  type FareSliceDetailsV2,
  UtaCategory,
} from "@b2bportal/air-shopping-api";
import {
  HtsAirCfarOffersSelectors,
  extractCfarMerchandisingRestrictions,
} from "@b2bportal/core-flights-cfar-offers";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  AirRestrictionStatus,
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import {
  collectRestrictions,
  localizedAmenityLabel,
  localizedUtaCategoryLabel,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FareRestrictions.module.scss";

const FARE_CLASS_DETAILS_I18N_KEY = {
  0: "fareClass.basicDetails",
  1: "fareClass.standardDetails",
  2: "fareClass.enhancedDetails",
  3: "fareClass.premiumDetails",
  4: "fareClass.luxuryDetails",
};

export interface FareRestrictionsComponentProps extends PlatformComponentProps {
  fareSlice: FareSliceDetailsV2;
  numberOfFareDetails?: number;
  showAllFareDetails?: boolean;
}

export const FareRestrictions = ({
  fareSlice,
  className,
  children,
}: FareRestrictionsComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FareRestrictions;

  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const selectedHtsCfarOffer = useSelector(
    HtsAirCfarOffersSelectors.selectCfarOfferWithSelectedStatus
  );

  const getAmenitiesLabel = React.useCallback(
    (key: keyof Amenities) => localizedAmenityLabel(key, t),
    [t]
  );
  const getUtaLabel = React.useCallback(
    (category: UtaCategory) => localizedUtaCategoryLabel(category, t),
    [t]
  );

  const cfarMerchandisingRestrictions = useMemo(
    () =>
      selectedHtsCfarOffer?.offer?.merchandisingContents
        ? extractCfarMerchandisingRestrictions(
            selectedHtsCfarOffer.offer.merchandisingContents
          )
        : [],
    [selectedHtsCfarOffer]
  );

  const htsCfarSelected = selectedHtsCfarOffer?.offer != null;

  // When CFAR is selected, filter out cancellation UTAs at the source
  // (by category enum) before converting to restrictions. This is more
  // robust than filtering by localized label strings after conversion.
  const filteredAmenitiesUtas = useMemo(() => {
    if (!htsCfarSelected || fareSlice.amenitiesUtas == null) {
      return fareSlice.amenitiesUtas;
    }
    return {
      ...fareSlice.amenitiesUtas,
      utas: {
        ...fareSlice.amenitiesUtas.utas,
        utas: fareSlice.amenitiesUtas.utas.utas.filter(
          (uta) => uta.category !== UtaCategory.cancellation
        ),
      },
    };
  }, [htsCfarSelected, fareSlice.amenitiesUtas]);

  const fareDetailsList = useMemo(() => {
    if (filteredAmenitiesUtas == null) {
      return [];
    }

    const airlineRestrictions = collectRestrictions(
      filteredAmenitiesUtas,
      getUtaLabel,
      getAmenitiesLabel,
      { includeAmenities: false }
    );

    if (cfarMerchandisingRestrictions.length > 0) {
      // Insert CFAR merchandising restrictions after the first two airline
      // restrictions so they appear prominently near the top of the list.
      const insertIndex = Math.min(2, airlineRestrictions.length);
      return [
        ...airlineRestrictions.slice(0, insertIndex),
        ...cfarMerchandisingRestrictions,
        ...airlineRestrictions.slice(insertIndex),
      ];
    }

    return airlineRestrictions;
  }, [
    filteredAmenitiesUtas,
    getAmenitiesLabel,
    getUtaLabel,
    cfarMerchandisingRestrictions,
  ]);

  const fareDetailsIcon: { [k in AirRestrictionStatus]: ReactNode } = useMemo(
    () => ({
      [AirRestrictionStatus.INCLUDED]: (
        <Icon
          iconName={IconNameEnum.fareDetailsAvailable}
          className={cn("icon-available")}
        />
      ),
      [AirRestrictionStatus.PURCHASABLE]: (
        <Icon
          iconName={IconNameEnum.fareDetailsCharge}
          className={cn("icon-charge")}
        />
      ),
      [AirRestrictionStatus.UNAVAILABLE]: (
        <Icon
          iconName={IconNameEnum.fareDetailsUnavailable}
          className={cn("icon-unavailable")}
        />
      ),
      [AirRestrictionStatus.GENERIC]: (
        <Icon
          iconName={IconNameEnum.fareDetailsGeneric}
          className={cn("icon-generic")}
        />
      ),
      [AirRestrictionStatus.UNKNOWN]: null,
    }),
    [cn]
  );

  const fareDetails = useMemo(() => {
    return fareDetailsList.length > 0 ? (
      <>
        {fareDetailsList.map((restriction, i) => (
          <li
            className={cn("item")}
            key={`fare-details-${fareSlice.fareDetails}-${i}`}
          >
            {fareDetailsIcon[restriction.symbol]}
            <div className={cn("details")}>
              {restriction.hideName ? null : (
                <div className={cn("detail-name")}>{restriction.name}</div>
              )}
              {restriction.hideName &&
              typeof restriction.description === "string" ? (
                // HTML content is pre-sanitized by extractCfarMerchandisingRestrictions
                // via DOMPurify.sanitize() before being stored in IRestrictionProps.
                <div
                  className={cn("detail-description")}
                  dangerouslySetInnerHTML={{
                    __html: restriction.description,
                  }}
                />
              ) : (
                <div className={cn("detail-description")}>
                  {restriction.description}
                </div>
              )}
            </div>
          </li>
        ))}
      </>
    ) : fareSlice.fareShelf == null ? null : (
      <li className={cn("general-detail")}>
        {t(FARE_CLASS_DETAILS_I18N_KEY[fareSlice.fareShelf.rating])}
      </li>
    );
  }, [
    cn,
    fareDetailsIcon,
    fareDetailsList,
    fareSlice.fareDetails,
    fareSlice.fareShelf,
    t,
  ]);

  return (
    <div className={clsx(block, className)}>
      <ul className={cn("list")}>{fareDetails}</ul>
      {children}
    </div>
  );
};
